import React, { useEffect, useState } from 'react';
import { Clock, User, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import AdBanner from '../components/AdBanner';
import { Button } from '../components/ui/button';
import { usePageSEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Palinsesto = () => {
  const { t, language } = useTranslation();
  usePageSEO({
    it: {
      title: 'Palinsesto — Programmi della Settimana | Rete Italia',
      description: 'Il palinsesto settimanale di Rete Italia: tutti i programmi, gli orari e i conduttori, da lunedì a domenica.',
    },
    en: {
      title: 'Schedule — Weekly Programs | Rete Italia',
      description: 'The Rete Italia weekly schedule: all the programs, times and presenters, Monday to Sunday.',
    },
    basePath: '/palinsesto',
  });
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Get current day of week (0 = Sunday, 1 = Monday, etc.)
  useEffect(() => {
    const today = new Date().getDay();
    // Convert to our index (0 = Monday, 6 = Sunday)
    const dayIndex = today === 0 ? 6 : today - 1;
    setSelectedDayIndex(dayIndex);
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setError(false);
        const response = await axios.get(`${API}/schedule`);
        if (response.data && response.data.length > 0) {
          setSchedule(response.data);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Error fetching schedule:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, []);

  const goToPreviousDay = () => {
    setSelectedDayIndex((prev) => (prev === 0 ? 6 : prev - 1));
  };

  const goToNextDay = () => {
    setSelectedDayIndex((prev) => (prev === 6 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16 flex items-center justify-center">
        <p className="text-xl text-gray-600">{t('schedule.loading')}</p>
      </div>
    );
  }

  if (error || schedule.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">
            {language === 'it' ? 'Impossibile caricare il palinsesto' : 'Unable to load schedule'}
          </p>
          <Button onClick={() => window.location.reload()} className="bg-[#009246] hover:bg-[#007a3d]">
            {language === 'it' ? 'Riprova' : 'Try Again'}
          </Button>
        </div>
      </div>
    );
  }

  // Clamp selectedDayIndex to valid range based on data
  const safeDayIndex = schedule.length > 0 ? Math.min(selectedDayIndex, schedule.length - 1) : 0;
  const currentDay = schedule[safeDayIndex];
  const dayName = language === 'it' ? currentDay?.dayNameIt : currentDay?.dayNameEn;
  const programs = Array.isArray(currentDay?.programs) ? currentDay.programs : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        {/* Advertisement Banner */}
        <AdBanner className="mb-8" />

        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
              {t('schedule.title')}
            </span>
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            {t('schedule.subtitle')}
          </p>
        </div>

        {/* Day Selector */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-8">
          <div className="flex items-center justify-between">
            <Button
              onClick={goToPreviousDay}
              variant="ghost"
              size="icon"
              className="hover:bg-gray-100 flex-shrink-0"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>

            {/* Mobile: Show only current day name */}
            <div className="md:hidden flex-1 text-center">
              <span className="px-4 py-2 rounded-xl font-semibold bg-gradient-to-r from-[#009246] to-[#00b359] text-white shadow-lg">
                {schedule.length > 0 && (language === 'it' ? schedule[safeDayIndex]?.dayNameIt : schedule[safeDayIndex]?.dayNameEn)}
              </span>
            </div>

            {/* Desktop: Show all days */}
            <div className="hidden md:flex flex-1 justify-center gap-2 overflow-x-auto px-4">
              {schedule.map((day, index) => (
                <button
                  key={day.day || index}
                  onClick={() => setSelectedDayIndex(index)}
                  className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                    safeDayIndex === index
                      ? 'bg-gradient-to-r from-[#009246] to-[#00b359] text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {language === 'it' ? day.dayNameIt : day.dayNameEn}
                </button>
              ))}
            </div>

            <Button
              onClick={goToNextDay}
              variant="ghost"
              size="icon"
              className="hover:bg-gray-100 flex-shrink-0"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Current Day Header */}
        <div className="bg-gradient-to-r from-[#009246] to-[#00b359] rounded-2xl p-6 mb-8 text-white text-center">
          <div className="flex items-center justify-center gap-3">
            <Calendar className="w-8 h-8" />
            <h2 className="text-3xl font-bold">{dayName}</h2>
          </div>
        </div>

        {/* Schedule Grid */}
        <div className="space-y-4">
          {programs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
              {language === 'it' ? 'Nessun programma disponibile per questo giorno' : 'No programs available for this day'}
            </div>
          ) : programs.map((program, index) => (
            <div
              key={`${currentDay?.day || 'day'}-${index}`}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group"
            >
              <div className="flex items-stretch">
                {/* Time Badge */}
                <div 
                  className="p-4 md:p-6 text-white flex items-center justify-center min-w-[100px] md:min-w-[120px]"
                  style={{
                    background: index % 3 === 0 ? 'linear-gradient(135deg, #009246, #00b359)' 
                      : index % 3 === 1 ? 'linear-gradient(135deg, #CE2B37, #e63946)' 
                      : 'linear-gradient(135deg, #00247D, #0047AB)'
                  }}
                >
                  <div className="text-center">
                    <Clock className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-lg md:text-xl font-bold">{program.time}</p>
                  </div>
                </div>

                {/* Program Image (if available) */}
                {program.image && (
                  <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 overflow-hidden">
                    <img 
                      src={program.image} 
                      alt={program.host || program.program}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Program Details */}
                <div className="flex-1 p-4 md:p-6 flex flex-col justify-center">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-[#009246] transition-colors">
                    {program.program}
                  </h3>
                  {program.host && (
                    <div className="flex items-center gap-2 text-gray-600 mt-1">
                      <User className="w-4 h-4" />
                      <span className="text-sm md:text-base">{program.host}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note Section */}
        <div className="mt-8 bg-gradient-to-r from-[#00247D] to-[#0047AB] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">{t('schedule.note')}</h3>
          <p className="text-lg opacity-90">
            {t('schedule.noteText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Palinsesto;
